import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { SiteHeader, SiteFooter, AgLogo } from '../components/SiteLayout';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { ArrowRight, Eye, EyeSlash } from '@phosphor-icons/react';
import TI from '../components/brand/TI';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Portal-aware post-login destination (no duplicate auth — same login flow).
  //   /login?return=/hale/dashboard  → return path wins
  //   /login?portal=hale             → Hale brand dashboard
  //   default                        → /dashboard
  const params = new URLSearchParams(location.search);
  const returnTo = params.get('return') || location.state?.from?.pathname || null;
  const portal = params.get('portal');
  const isHale = portal === 'hale' || (returnTo && returnTo.includes('/hale'));
  const target = returnTo || (isHale ? '/hale/dashboard' : '/dashboard');

  // Already authenticated? Send them straight to the right dashboard.
  useEffect(() => {
    if (!authLoading && user) navigate(target, { replace: true });
  }, [authLoading, user, target, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    const result = await login(email, password);
    if (result.success) navigate(target);
    else setError(result.error);
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#F9FAFB]">
      <SiteHeader />
      <main className="flex-1 flex items-center justify-center py-12 px-6">
        <div className="w-full max-w-md">
          <div className="mb-8 text-center">
            <h1 className="text-3xl font-light tracking-tight text-[#0B1426] mb-2"
                style={{ fontFamily: '"Cormorant Garamond", "Times New Roman", serif' }}
                data-testid="login-h1">
              {isHale ? 'Brand Sign In' : 'Client Sign In'}
            </h1>
            <p className="text-[13.5px] text-[#5B6577]">
              {isHale
                ? 'Access your Hale League brand dashboard.'
                : <>Access the <TI color="#E63946" /> client portal.</>}
            </p>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-[#FDE8E8] border border-[#F8B4B4] rounded-sm">
              <p className="text-sm text-[#9B1C1C]" data-testid="login-error">{error}</p>
            </div>
          )}

          <div className="bg-white p-8 border border-gray-200 rounded-sm">
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-sm font-semibold text-[#111827]">Email address</Label>
                <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" required data-testid="login-email-input" className="h-11 rounded-sm border-gray-200 focus:ring-2 focus:ring-blue-600 focus:border-transparent" />
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password" className="text-sm font-semibold text-[#111827]">Password</Label>
                  <Link to="/forgot-password" className="text-sm text-[#2563EB] hover:text-[#1D4ED8] font-medium" data-testid="forgot-password-link">Forgot password?</Link>
                </div>
                <div className="relative">
                  <Input id="password" type={showPassword ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" required data-testid="login-password-input" className="h-11 rounded-sm border-gray-200 focus:ring-2 focus:ring-blue-600 focus:border-transparent pr-12" />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600" data-testid="toggle-password-visibility">
                    {showPassword ? <EyeSlash size={20} /> : <Eye size={20} />}
                  </button>
                </div>
              </div>
              <Button type="submit" disabled={loading} data-testid="login-submit-btn" className="w-full h-11 btn-primary flex items-center justify-center gap-2">
                {loading ? <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <>Sign in <ArrowRight size={18} weight="bold" /></>}
              </Button>
            </form>
          </div>

          <p className="mt-6 text-center text-[#4B5563]">
            Don't have an account?{' '}
            <Link to="/register" className="text-[#2563EB] hover:text-[#1D4ED8] font-semibold" data-testid="register-link">Create one</Link>
          </p>
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}
